/**
 * /diagnostics/scanner — Scanner / Barcode diagnostics submission (#1873).
 *
 * Rep workflow:
 *   1. Land on this page (support directed them here)
 *   2. Tap "Start Scanner" → camera activates with debug HUD showing
 *      what ZBar + MSI decoder see on every frame
 *   3. Point at the problem tag
 *   4. Tap "Capture for Support" — snapshots the current frame to base64
 *      JPEG + the MSI attempt trace. Up to 5 captures.
 *   5. (Optional) Add a free-form note + "expected value" per capture
 *   6. Tap "Send to Support" — assembles full diagnostics payload with
 *      scanner context block + base64 frames + traces, POSTs to
 *      /api/diagnostics/submit/
 *
 * Captured frames are JPEG quality 0.7, capped at 200 KB each. Total
 * payload capped at 3 MB by the server (#1873). If a frame exceeds its
 * cap we re-encode at lower quality before keeping it.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarcodeDetectorPolyfill } from '@undecaf/barcode-detector-polyfill';
import { setModuleArgs } from '@undecaf/zbar-wasm';
import zbarWasmUrl from '@undecaf/zbar-wasm/dist/zbar.wasm?url';
import { decodeMSIFromImageDataWithTraces } from '../utils/scanner/msi';
import type { MSIDecodeAttemptTrace, MSIDecodeResult } from '../utils/scanner/msi';
import { buildOverlayBoxes, paintOverlay, type MsiBestBox, type ZbarBox } from '../utils/scanner/scanOverlay';
import { classifyScan, summarizeScans, type ScanDiff } from '../utils/scanner/scanDiff';
import { collectDiagnostics } from '../utils/diagnosticsCollector';
import DiagnosticAttachments, { DiagnosticAttachmentsValue } from '../components/DiagnosticAttachments';
import { diagnosticsApi } from '../services/api';

setModuleArgs({
  locateFile: (file: string) => (file.endsWith('.wasm') ? zbarWasmUrl : file),
});

// Same minimal format set as the production scanner (#1872) — UPC/EAN
// for products, Code 128 for internal SKUs. MSI runs separately below.
const ZBAR_FORMATS = ['upc_a', 'upc_e', 'ean_13', 'code_128'];

const MAX_CAPTURE_WIDTH = 1280;
const MAX_CAPTURES = 5;
const MAX_BYTES_PER_FRAME = 200 * 1024;

interface ZBarResult {
  format: string;
  rawValue: string;
  quality: number;
  /** Spatial location (#2205) — for the decode-region overlay. */
  boundingBox?: { x: number; y: number; width: number; height: number } | null;
  cornerPoints?: Array<{ x: number; y: number }> | null;
}

interface CaptureFrame {
  id: string;
  capturedAt: string;
  /** ms between camera-open and this capture. iPhone autofocus can take ~1500ms. */
  frameAgeMs: number;
  frameJpegBase64: string;
  frameJpegBytes: number;
  frameDims: { w: number; h: number };
  /** Mean luminance of the captured frame in [0, 255]. Underexposed frames decode badly. */
  avgBrightness: number;
  /** Track.getSettings() snapshot — actual resolution achieved + focus/exposure. */
  cameraSettings: Record<string, unknown> | null;
  /** Track.getCapabilities() — what the camera CAN do, vs what it chose to do. */
  cameraCapabilities: Record<string, unknown> | null;
  zbar: {
    attempts: number;
    results: ZBarResult[];
    bestQuality: number;
  };
  msi: {
    best: MSIDecodeResult | null;
    attempts: MSIDecodeAttemptTrace[];
    scanlinesExtracted: number;
    /** Top "almost made it" candidates with their raw run widths. */
    promising: MSIDecodeAttemptTrace[];
    /** Vote tally across scanlines — which digit string showed up how many times. */
    voteSummary: Array<{ digits: string; checksum: string; count: number; confidence: number }>;
    /** Pixel bbox of the winning decode + cut-off/edge flags (#2205). */
    bestBox: MsiBestBox | null;
  };
  /** Rep-entered free-form note. */
  repNote: string;
  /** Rep-entered expected value (optional — they may not know what the barcode encodes). */
  expectedValue: string;
  /** Calibration target value in effect when this frame was captured (#2205). */
  targetValue: string;
  /** Decoded-vs-target classification for this frame (#2205), null if no target. */
  diff: ScanDiff | null;
  /** True if captured automatically on a live decode hit vs a manual tap (#2205). */
  auto?: boolean;
}

type Phase = 'idle' | 'scanning' | 'sending' | 'sent' | 'error';

export default function DiagnosticsScanner() {
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const detectorRef = useRef<BarcodeDetectorPolyfill | null>(null);
  const rafRef = useRef<number | null>(null);
  const mountedRef = useRef(true);
  const cameraStartedAtRef = useRef<number | null>(null);
  const lastHitValueRef = useRef<string>('');

  const [phase, setPhase] = useState<Phase>('idle');
  const [scanning, setScanning] = useState(false);
  const [captures, setCaptures] = useState<CaptureFrame[]>([]);
  // #2205 calibration — the rep types the value printed on the label; every
  // capture is then classified decoded-vs-target so we can see HOW it's wrong.
  const [targetValue, setTargetValue] = useState('');
  const targetRef = useRef('');
  targetRef.current = targetValue;
  // Auto-capture the frame the instant a decoder gets a hit (RMR: manual tap
  // can't catch the decodable moment). Throttled + capped via the refs below.
  const [autoCapture, setAutoCapture] = useState(true);
  const autoCaptureRef = useRef(true);
  autoCaptureRef.current = autoCapture;
  const captureCountRef = useRef(0);
  captureCountRef.current = captures.length;
  const lastAutoCaptureAtRef = useRef(0);
  const autoCaptureBusyRef = useRef(false);
  // Submission-level attachments (#1965). Distinct from per-capture
  // repNote which annotates a single barcode attempt; these are the
  // overall narrative + iPhone screenshots for the whole report.
  const [attachments, setAttachments] = useState<DiagnosticAttachmentsValue>({
    notes: '',
    screenshots: [],
  });
  const [liveStats, setLiveStats] = useState({
    frames: 0,
    lastZbarFormat: '',
    lastZbarValue: '',
    lastZbarQuality: 0,
    lastMsiResult: '',
    lastMsiAttempts: 0,
    lastMsiScanlines: 0,
    // Session tallies (#1873). Helps the rep see "is the decoder
    // working at all" at a glance — 5 hits / 200 frames says it's
    // working but framing is unstable; 0 hits / 200 frames says
    // something's actually wrong.
    zbarHits: 0,
    msiHits: 0,
    misses: 0,
    cameraError: '',
  });
  // Sticky last-hit banner. Persists for 3 seconds after the most recent
  // successful decode so the rep can clearly see "got it" without having
  // to monitor the HUD every frame. `flash` triggers a 1s green border
  // pulse when a NEW value lands (different from the previous one).
  const [hit, setHit] = useState<{ value: string; source: 'zbar' | 'msi'; meta: string; at: number } | null>(null);
  const [flash, setFlash] = useState(false);
  const hitClearTimerRef = useRef<number | null>(null);
  const flashClearTimerRef = useRef<number | null>(null);
  const [refId, setRefId] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // -------- camera lifecycle --------

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      stopCamera();
      if (hitClearTimerRef.current !== null) window.clearTimeout(hitClearTimerRef.current);
      if (flashClearTimerRef.current !== null) window.clearTimeout(flashClearTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopCamera = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    const v = videoRef.current;
    if (v) {
      try {
        v.pause();
      } catch {
        // ignore
      }
      v.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(async () => {
    if (!mountedRef.current) return;
    setLiveStats((s) => ({ ...s, cameraError: '' }));

    try {
      if (!detectorRef.current) {
        detectorRef.current = new BarcodeDetectorPolyfill({
          formats: ZBAR_FORMATS,
          zbar: { enableCache: false },
        });
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      if (!mountedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      streamRef.current = stream;
      const v = videoRef.current;
      if (!v) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      v.srcObject = stream;
      v.playsInline = true;
      v.muted = true;
      await v.play();
      cameraStartedAtRef.current = Date.now();
      setScanning(true);
      rafRef.current = requestAnimationFrame(detectLoop);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setLiveStats((s) => ({ ...s, cameraError: msg.slice(0, 200) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Per-frame: draw to canvas, run ZBar + MSI decoders, update HUD.
  // Doesn't auto-capture — rep taps the Capture button.
  const detectLoop = useCallback(async () => {
    rafRef.current = null;
    if (!mountedRef.current || !scanning) return;
    const v = videoRef.current;
    const det = detectorRef.current;
    if (!v || !det || v.videoWidth === 0) {
      rafRef.current = requestAnimationFrame(detectLoop);
      return;
    }

    const { canvas, ctx, cw, ch } = ensureCanvas(v);
    if (!canvas || !ctx) {
      rafRef.current = requestAnimationFrame(detectLoop);
      return;
    }
    ctx.drawImage(v, 0, 0, cw, ch);

    let zbarResults: ZBarResult[] = [];
    try {
      zbarResults = (await det.detect(canvas as unknown as CanvasImageSource)) as unknown as ZBarResult[];
    } catch {
      zbarResults = [];
    }
    const bestZ = zbarResults.length > 0 ? zbarResults.reduce((a, b) => (a.quality > b.quality ? a : b)) : null;

    const imageData = ctx.getImageData(0, 0, cw, ch);
    const msiRun = decodeMSIFromImageDataWithTraces(imageData);

    const zbarHitThisFrame = bestZ && bestZ.quality >= 30;
    const msiHitThisFrame = !!msiRun.best;
    const anyHit = zbarHitThisFrame || msiHitThisFrame;

    setLiveStats((s) => ({
      ...s,
      frames: s.frames + 1,
      lastZbarFormat: bestZ?.format ?? '',
      lastZbarValue: bestZ?.rawValue ?? '',
      lastZbarQuality: bestZ?.quality ?? 0,
      lastMsiResult: msiRun.best ? `${msiRun.best.digits} (${msiRun.best.checksum})` : '',
      lastMsiAttempts: msiRun.attempts.length,
      lastMsiScanlines: msiRun.scanlinesExtracted,
      zbarHits: s.zbarHits + (zbarHitThisFrame ? 1 : 0),
      msiHits: s.msiHits + (msiHitThisFrame ? 1 : 0),
      misses: s.misses + (anyHit ? 0 : 1),
    }));

    // Sticky-banner + flash on any hit (#1873 HUD improvement).
    if (anyHit) {
      const hitValue = msiHitThisFrame
        ? msiRun.best!.digits
        : bestZ!.rawValue;
      const hitSource: 'zbar' | 'msi' = msiHitThisFrame ? 'msi' : 'zbar';
      const hitMeta = msiHitThisFrame
        ? `MSI ${msiRun.best!.checksum} · conf ${Math.round(msiRun.best!.confidence * 100)}`
        : `${bestZ!.format.toUpperCase()} · q ${bestZ!.quality}`;
      const isNewValue = hitValue !== lastHitValueRef.current;
      lastHitValueRef.current = hitValue;
      setHit({ value: hitValue, source: hitSource, meta: hitMeta, at: Date.now() });
      // Refresh 3s clear timer
      if (hitClearTimerRef.current !== null) window.clearTimeout(hitClearTimerRef.current);
      hitClearTimerRef.current = window.setTimeout(() => {
        if (mountedRef.current) setHit(null);
      }, 3000);
      if (isNewValue) {
        setFlash(true);
        if (flashClearTimerRef.current !== null) window.clearTimeout(flashClearTimerRef.current);
        flashClearTimerRef.current = window.setTimeout(() => {
          if (mountedRef.current) setFlash(false);
        }, 600);
      }
    }

    // Auto-capture the frame the instant a decoder gets a hit — the canvas
    // already holds THIS frame and we have its results, so we save the exact
    // decodable moment without a manual tap (RMR: timing makes manual capture
    // miss it). Throttled + capped; encode is synchronous so it captures this
    // frame before the next drawImage.
    if (
      anyHit &&
      autoCaptureRef.current &&
      !autoCaptureBusyRef.current &&
      captureCountRef.current < MAX_CAPTURES &&
      Date.now() - lastAutoCaptureAtRef.current > 700
    ) {
      autoCaptureBusyRef.current = true;
      lastAutoCaptureAtRef.current = Date.now();
      const zb = zbarResults.map(normalizeZbar);
      void finalizeCaptureRef.current(canvas, ctx, cw, ch, imageData, zb, msiRun, true).finally(() => {
        autoCaptureBusyRef.current = false;
      });
    }

    if (mountedRef.current && scanning) {
      rafRef.current = requestAnimationFrame(detectLoop);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanning]);

  // Restart loop whenever `scanning` flips
  useEffect(() => {
    if (scanning && rafRef.current === null) {
      rafRef.current = requestAnimationFrame(detectLoop);
    }
    if (!scanning && rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, [scanning, detectLoop]);

  // -------- capture --------

  // Build a CaptureFrame from the CURRENT canvas + decode results and store it
  // (overlay painted, telemetry + diff attached). Shared by the manual Capture
  // button and the auto-capture-on-decode path so both produce identical frames.
  const finalizeCapture = useCallback(
    async (
      canvas: HTMLCanvasElement,
      ctx: CanvasRenderingContext2D,
      cw: number,
      ch: number,
      imageData: ImageData,
      zbarResults: ZBarResult[],
      msi: ReturnType<typeof decodeMSIFromImageDataWithTraces>,
      auto: boolean,
    ) => {
      const avgBrightness = computeAvgBrightness(imageData);
      // Decoded-vs-target classification + decode-region overlay, painted onto
      // the frame BEFORE JPEG encode so the saved image shows where each decoder
      // matched (and whether it ran off a frame edge).
      const bestValue = msi.best?.digits ?? (zbarResults[0]?.rawValue ?? '');
      const diff: ScanDiff | null = targetRef.current ? classifyScan(targetRef.current, bestValue) : null;
      const zbarBoxes: ZbarBox[] = zbarResults
        .filter((z) => z.boundingBox || z.cornerPoints)
        .map((z) => ({ rawValue: z.rawValue, format: z.format, boundingBox: z.boundingBox, cornerPoints: z.cornerPoints }));
      paintOverlay(ctx, buildOverlayBoxes(msi.bestBox, zbarBoxes));

      const track = streamRef.current?.getVideoTracks()[0] ?? null;
      let cameraSettings: Record<string, unknown> | null = null;
      let cameraCapabilities: Record<string, unknown> | null = null;
      try {
        cameraSettings = (track?.getSettings() ?? null) as Record<string, unknown> | null;
      } catch {
        // ignore
      }
      try {
        // @ts-expect-error - getCapabilities() not in TS lib on older targets
        cameraCapabilities = track?.getCapabilities?.() ?? null;
      } catch {
        // ignore
      }

      const encoded = await encodeFrameUnderCap(canvas, MAX_BYTES_PER_FRAME);
      const frameAgeMs = cameraStartedAtRef.current ? Date.now() - cameraStartedAtRef.current : 0;

      const frame: CaptureFrame = {
        id: `cap-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        capturedAt: new Date().toISOString(),
        frameAgeMs,
        frameJpegBase64: encoded.base64,
        frameJpegBytes: encoded.bytes,
        frameDims: { w: cw, h: ch },
        avgBrightness,
        cameraSettings,
        cameraCapabilities,
        auto,
        zbar: {
          attempts: 1,
          results: zbarResults,
          bestQuality: zbarResults.length > 0 ? Math.max(...zbarResults.map((r) => r.quality)) : 0,
        },
        msi: {
          best: msi.best,
          attempts: msi.attempts,
          scanlinesExtracted: msi.scanlinesExtracted,
          promising: msi.promising,
          voteSummary: msi.voteSummary,
          bestBox: msi.bestBox,
        },
        repNote: '',
        expectedValue: targetRef.current,
        targetValue: targetRef.current,
        diff,
      };
      // Functional cap so concurrent (auto + manual) captures never exceed MAX.
      let stored = false;
      setCaptures((c) => {
        if (c.length >= MAX_CAPTURES) return c;
        stored = true;
        return [...c, frame];
      });
      return stored;
    },
    [],
  );
  // Ref so detectLoop (defined above) can call the latest finalizeCapture
  // without being recreated on every render.
  const finalizeCaptureRef = useRef(finalizeCapture);
  finalizeCaptureRef.current = finalizeCapture;

  const handleCapture = useCallback(async () => {
    const v = videoRef.current;
    const det = detectorRef.current;
    if (!v || !det || captureCountRef.current >= MAX_CAPTURES) return;
    const { canvas, ctx, cw, ch } = ensureCanvas(v);
    if (!canvas || !ctx) return;
    ctx.drawImage(v, 0, 0, cw, ch);
    let zbarResults: ZBarResult[] = [];
    try {
      const raw = (await det.detect(canvas as unknown as CanvasImageSource)) as unknown[];
      zbarResults = raw.map(normalizeZbar);
    } catch {
      zbarResults = [];
    }
    const imageData = ctx.getImageData(0, 0, cw, ch);
    const msi = decodeMSIFromImageDataWithTraces(imageData);
    await finalizeCapture(canvas, ctx, cw, ch, imageData, zbarResults, msi, false);
  }, [finalizeCapture]);

  const updateCapture = (id: string, patch: Partial<CaptureFrame>) => {
    setCaptures((c) => c.map((f) => (f.id === id ? { ...f, ...patch } : f)));
  };

  const removeCapture = (id: string) => {
    setCaptures((c) => c.filter((f) => f.id !== id));
  };

  // -------- submit --------

  const handleSubmit = useCallback(async () => {
    if (captures.length === 0) return;
    setPhase('sending');
    setSubmitError(null);
    stopCamera();
    setScanning(false);

    try {
      const report = await collectDiagnostics({
        context: {
          type: 'scanner',
          scanner: {
            captures: captures.map((c) => ({
              id: c.id,
              captured_at: c.capturedAt,
              frame_age_ms: c.frameAgeMs,
              frame_jpeg_base64: c.frameJpegBase64,
              frame_jpeg_bytes: c.frameJpegBytes,
              frame_dims: c.frameDims,
              avg_brightness: c.avgBrightness,
              camera_settings: c.cameraSettings,
              camera_capabilities: c.cameraCapabilities,
              zbar: c.zbar,
              msi: c.msi,
              rep_note: c.repNote || undefined,
              expected_value: c.expectedValue || undefined,
              target_value: c.targetValue || undefined,
              diff: c.diff || undefined,
            })),
          },
          attachments: {
            notes: attachments.notes.trim(),
            screenshots: attachments.screenshots,
          },
        },
      });
      const result = await diagnosticsApi.submit(report);
      setRefId(result.report_id);
      setPhase('sent');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
      setPhase('error');
    }
  }, [captures, stopCamera]);

  const totalCaptureBytes = captures.reduce((s, c) => s + c.frameJpegBytes, 0);

  // -------- render --------

  return (
    <div className="min-h-screen bg-slate-50 pb-32">
      <div className="bg-white shadow-sm">
        <div className="flex items-center gap-4 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-slate-100"
            aria-label="Back"
          >
            <svg className="h-6 w-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-slate-800">Scanner Diagnostics</h1>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {phase === 'idle' && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
            <p className="font-medium mb-1">How this works</p>
            <ol className="ml-4 list-decimal space-y-1">
              <li>Start the scanner and point at the problem tag</li>
              <li>Watch the live HUD to see what the decoders find (or don't)</li>
              <li>Tap "Capture for Support" up to {MAX_CAPTURES} times</li>
              <li>(Optional) Add a note + expected value per capture</li>
              <li>Tap "Send to Support" — bundles photos, decoder traces, GPS, network test, device info</li>
            </ol>
          </div>
        )}

        {phase === 'sent' ? (
          <div className="rounded-xl bg-green-50 border border-green-200 p-4 text-center">
            <svg className="mx-auto h-12 w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-2 font-semibold text-green-800">Sent</p>
            {refId !== null && <p className="mt-1 text-xs font-mono text-green-700">Reference: #{refId}</p>}
            <p className="mt-2 text-sm text-green-700">
              When you contact support, mention this reference number.
            </p>
            <button
              onClick={() => navigate('/diagnostics')}
              className="mt-4 rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-800"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {/* #2205 Calibration — type the printed label value; each capture
                is scored decoded-vs-target so we can see HOW it's wrong. */}
            <div className="rounded-xl bg-white p-4 shadow-card space-y-2">
              <label className="block text-sm font-medium text-slate-700">Calibration target (optional)</label>
              <p className="text-xs text-slate-500">
                Type the value printed on the shelf tag, then capture it a few times. Each scan is
                scored against it, and the saved frame is marked with where the decoders read.
              </p>
              <input
                type="text"
                inputMode="numeric"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                placeholder="e.g. 0106943"
                className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-mono focus:border-primary-500 focus:outline-none"
              />
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input
                  type="checkbox"
                  checked={autoCapture}
                  onChange={(e) => setAutoCapture(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                />
                Auto-save a frame whenever it decodes (no need to tap Capture at the right instant)
              </label>
              {targetValue && captures.some((c) => c.diff) && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-2 text-xs">
                  <span className="font-medium text-slate-700">Verdict: </span>
                  <span className="text-slate-600">
                    {summarizeScans(captures.filter((c) => c.diff).map((c) => c.diff!)).verdict}
                  </span>
                </div>
              )}
            </div>

            {/* Camera + HUD */}
            <div className="rounded-xl bg-slate-900 p-3 shadow-card overflow-hidden">
              <div className="relative aspect-[4/3] bg-black rounded-lg overflow-hidden">
                <video
                  ref={videoRef}
                  playsInline
                  muted
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
                {scanning && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <div className="border-2 border-white/70 rounded-md" style={{ width: '85%', height: '40%' }} />
                  </div>
                )}
                {/* Detection flash — full-frame green pulse on NEW hit */}
                {flash && (
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      boxShadow: 'inset 0 0 0 6px rgb(34 197 94)',
                      animation: 'none',
                    }}
                  />
                )}

                {/* Top HUD: session tally — at-a-glance "is the decoder working" */}
                {scanning && (
                  <div className="absolute left-2 right-2 top-2 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md bg-black/70 px-3 py-1.5 font-mono text-xs text-slate-100">
                    <span className="text-green-300">
                      ✓ {liveStats.zbarHits + liveStats.msiHits}
                    </span>
                    <span className="text-slate-400">
                      hits ({liveStats.zbarHits} zbar · {liveStats.msiHits} msi)
                    </span>
                    <span className="text-slate-500">/</span>
                    <span className="text-amber-300">{liveStats.misses} misses</span>
                    <span className="text-slate-500">·</span>
                    <span className="text-slate-400">{liveStats.frames} frames</span>
                    <span className="text-slate-500">·</span>
                    <span className="text-slate-400">
                      msi {liveStats.lastMsiAttempts} attempts / {liveStats.lastMsiScanlines} sl
                    </span>
                  </div>
                )}

                {/* Sticky last-hit banner (persists 3s after most recent decode).
                    Bottom of camera view so it doesn't cover what the rep
                    is trying to scan. Large enough to read without effort. */}
                {scanning && hit && (
                  <div
                    style={{
                      position: 'absolute',
                      left: 8,
                      right: 8,
                      bottom: 8,
                      borderRadius: 8,
                      background: 'rgba(22, 163, 74, 0.95)',
                      color: '#fff',
                      padding: '8px 12px',
                      boxShadow: '0 2px 12px rgba(0,0,0,.35)',
                      pointerEvents: 'none',
                    }}
                  >
                    <div style={{ fontSize: 10, textTransform: 'uppercase', opacity: 0.85, letterSpacing: '.05em', fontWeight: 600 }}>
                      Detected · {hit.meta}
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'ui-monospace, monospace', lineHeight: 1.15, marginTop: 2 }}>
                      {hit.value}
                    </div>
                  </div>
                )}
                {liveStats.cameraError && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/80 p-4 text-center text-sm text-red-300">
                    {liveStats.cameraError}
                  </div>
                )}
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                {scanning ? (
                  <button
                    onClick={() => {
                      setScanning(false);
                      stopCamera();
                    }}
                    className="rounded-xl bg-slate-700 py-3 text-sm font-medium text-white hover:bg-slate-600"
                  >
                    Stop Scanner
                  </button>
                ) : (
                  <button
                    onClick={startCamera}
                    disabled={phase === 'sending'}
                    className="rounded-xl bg-primary-600 py-3 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    Start Scanner
                  </button>
                )}
                <button
                  onClick={handleCapture}
                  disabled={!scanning || captures.length >= MAX_CAPTURES}
                  className="rounded-xl bg-orange-600 py-3 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-40"
                >
                  Capture ({captures.length}/{MAX_CAPTURES})
                </button>
              </div>
            </div>

            {/* Captures list */}
            {captures.length > 0 && (
              <div className="rounded-xl bg-white p-4 shadow-card space-y-3">
                <h3 className="text-sm font-medium text-slate-700">
                  Captures ({captures.length}/{MAX_CAPTURES}) — {(totalCaptureBytes / 1024).toFixed(0)} KB total
                </h3>
                {captures.map((c, idx) => (
                  <div key={c.id} className="rounded-lg border border-slate-200 p-3 space-y-2">
                    <div className="flex items-start gap-3">
                      <img
                        src={`data:image/jpeg;base64,${c.frameJpegBase64}`}
                        alt={`Capture ${idx + 1}`}
                        className="h-20 w-20 flex-shrink-0 rounded object-cover"
                      />
                      <div className="flex-1 text-xs">
                        <div className="font-medium text-slate-700">
                          Capture {idx + 1}
                          {c.auto && (
                            <span className="ml-1.5 rounded bg-primary-100 px-1 py-0.5 text-[9px] font-medium text-primary-700">
                              auto
                            </span>
                          )}
                        </div>
                        <div className="text-slate-500">
                          {(c.frameJpegBytes / 1024).toFixed(0)} KB · {c.frameDims.w}×{c.frameDims.h}
                        </div>
                        <div className="mt-1 text-slate-600">
                          ZBar: {c.zbar.results.length > 0 ? `${c.zbar.results[0].format} ${c.zbar.results[0].rawValue}` : 'no match'}
                        </div>
                        <div className="text-slate-600">
                          MSI: {c.msi.best ? c.msi.best.digits : 'no match'} ({c.msi.attempts.length} attempts)
                        </div>
                        {c.diff && (
                          <div
                            className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-medium ${
                              c.diff.kind === 'exact'
                                ? 'bg-green-100 text-green-700'
                                : c.diff.kind === 'no-read'
                                  ? 'bg-slate-100 text-slate-600'
                                  : 'bg-amber-100 text-amber-800'
                            }`}
                          >
                            {diffLabel(c.diff)}
                          </div>
                        )}
                        {c.msi.bestBox && (c.msi.bestBox.touchedLeftEdge || c.msi.bestBox.touchedRightEdge) && (
                          <div className="mt-0.5 text-[10px] text-amber-700">
                            ⚠ decode ran off the {c.msi.bestBox.touchedRightEdge ? 'right' : 'left'} edge — possible partial read
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => removeCapture(c.id)}
                        className="rounded p-1 text-red-500 hover:bg-red-50"
                        aria-label="Remove capture"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                    <input
                      type="text"
                      value={c.expectedValue}
                      onChange={(e) => updateCapture(c.id, { expectedValue: e.target.value })}
                      placeholder="Expected value (optional — leave blank if unsure)"
                      className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                    />
                    <input
                      type="text"
                      value={c.repNote}
                      onChange={(e) => updateCapture(c.id, { repNote: e.target.value })}
                      placeholder="Note (optional — e.g. 'tag at PX #15 cosmetics')"
                      className="block w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none"
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Submit */}
            {captures.length > 0 && (
              <div className="rounded-xl bg-white p-4 shadow-card space-y-3">
                <p className="text-xs text-slate-500">
                  Submitting includes your device info, app version, network state, current GPS, and all captured frames + decoder traces.
                </p>
                <DiagnosticAttachments
                  value={attachments}
                  onChange={setAttachments}
                  disabled={phase === 'sending'}
                  notesLabel="More details & screenshots"
                  notesHint="Anything else support should know about this scan? Add iPhone screenshots of what you tried to scan or the error you saw."
                />
                {submitError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{submitError}</div>
                )}
                <button
                  onClick={handleSubmit}
                  disabled={phase === 'sending'}
                  className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-600 to-primary-700 py-3 font-semibold text-white shadow-lg disabled:opacity-50"
                >
                  {phase === 'sending' ? (
                    <>
                      <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Sending…
                    </>
                  ) : (
                    'Send to Support'
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Normalize a BarcodeDetector result into a plain, JSON-serializable shape
 * (#2205). The polyfill returns boundingBox as a DOMRectReadOnly whose props
 * aren't enumerable, so it would stringify to {} in the telemetry payload —
 * copy out x/y/width/height + cornerPoints explicitly.
 */
function normalizeZbar(r: unknown): ZBarResult {
  const o = (r ?? {}) as {
    format?: string;
    rawValue?: string;
    quality?: number;
    boundingBox?: { x?: number; y?: number; width?: number; height?: number } | null;
    cornerPoints?: Array<{ x?: number; y?: number }> | null;
  };
  const bb = o.boundingBox;
  return {
    format: o.format ?? '',
    rawValue: o.rawValue ?? '',
    quality: o.quality ?? 0,
    boundingBox: bb
      ? { x: bb.x ?? 0, y: bb.y ?? 0, width: bb.width ?? 0, height: bb.height ?? 0 }
      : null,
    cornerPoints: o.cornerPoints ? o.cornerPoints.map((p) => ({ x: p.x ?? 0, y: p.y ?? 0 })) : null,
  };
}

/** Human-readable label for a decoded-vs-target classification (#2205). */
function diffLabel(diff: ScanDiff): string {
  switch (diff.kind) {
    case 'exact':
      return 'exact ✓';
    case 'no-read':
      return 'no read';
    case 'leading-zero-missing':
      return `dropped leading ${(diff.missing ?? '0').length > 1 ? 'zeros' : 'zero'}`;
    case 'leading-missing':
      return `dropped leading ${diff.missing ?? ''}`;
    case 'truncated':
      return `truncated (missing ${diff.missing ?? ''})`;
    case 'extra':
      return `extra ${diff.extra ?? ''}`;
    case 'mismatch':
      return 'mismatch (way off)';
  }
}

function ensureCanvas(v: HTMLVideoElement): { canvas: HTMLCanvasElement | null; ctx: CanvasRenderingContext2D | null; cw: number; ch: number } {
  const vw = v.videoWidth;
  const vh = v.videoHeight;
  if (vw === 0 || vh === 0) return { canvas: null, ctx: null, cw: 0, ch: 0 };
  const scale = vw > MAX_CAPTURE_WIDTH ? MAX_CAPTURE_WIDTH / vw : 1;
  const cw = Math.round(vw * scale);
  const ch = Math.round(vh * scale);
  // Reuse a single offscreen canvas per page lifetime to avoid GC churn.
  const w = window as unknown as { __diag_canvas?: HTMLCanvasElement };
  if (!w.__diag_canvas) {
    w.__diag_canvas = document.createElement('canvas');
  }
  const canvas = w.__diag_canvas;
  if (canvas.width !== cw) canvas.width = cw;
  if (canvas.height !== ch) canvas.height = ch;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  return { canvas, ctx, cw, ch };
}

/**
 * Mean luminance of an ImageData (perceptual approximation). Samples
 * every 16th pixel to stay fast on 1080p frames. Returns 0..255.
 * Useful in diagnostics: very low avg + low contrast frames decode
 * badly, and we want to know that vs blaming the decoder.
 */
function computeAvgBrightness(img: ImageData): number {
  const d = img.data;
  let sum = 0;
  let n = 0;
  // Each pixel = 4 bytes (RGBA). Step 16 pixels = 64 bytes.
  for (let i = 0; i < d.length; i += 64) {
    sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    n += 1;
  }
  return n > 0 ? sum / n : 0;
}

/**
 * Iteratively re-encode the canvas as JPEG, lowering quality until the
 * output fits under `maxBytes`. Quality starts at 0.7 and drops by 0.1
 * each iteration; minimum 0.3.
 */
async function encodeFrameUnderCap(
  canvas: HTMLCanvasElement,
  maxBytes: number,
): Promise<{ base64: string; bytes: number }> {
  let quality = 0.7;
  while (quality >= 0.3) {
    const dataUrl = canvas.toDataURL('image/jpeg', quality);
    // dataUrl is "data:image/jpeg;base64,<...>"; strip prefix.
    const comma = dataUrl.indexOf(',');
    const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
    const bytes = Math.round((b64.length * 3) / 4); // approximate decoded size
    if (bytes <= maxBytes) return { base64: b64, bytes };
    quality -= 0.1;
  }
  // Last attempt at minimum quality, accept whatever size.
  const dataUrl = canvas.toDataURL('image/jpeg', 0.3);
  const comma = dataUrl.indexOf(',');
  const b64 = comma >= 0 ? dataUrl.slice(comma + 1) : dataUrl;
  return { base64: b64, bytes: Math.round((b64.length * 3) / 4) };
}

